import useAgentInterception from '../../../hooks/agentInterception/useAgentInterception.hooks';
import { selectIsAgentActionBlocked } from '../../../redux/features/agentMode/agentMode.selector';
import logger from '../../../utils/logger';
import { AgentInterceptionType } from '../../../reducers/messagingSlice';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { sendSpokenPrompt } from '../../../reducers/spokenPromptThunk';
import { TopLevelMenuItem } from '../../../utils/menu';
import { shouldSkipDonationPrompt } from '../../../utils/TTSMessage';
import { selectOrderValues } from '../../../selectors/order';
import { SLASH_COMMAND_REMINDERS } from '../../../utils/constants';
import {
  selectExperimentAssignment,
  selectHasCheckTotal,
  selectPromptAskCounts,
  selectPromptOverrides,
  selectWithheldConstraints,
} from '../../../redux/features/experiments/experiments.selector';
import {
  buildUpsellExperimentStamp,
  getSlashCommandSpokenPrompt,
  isConstraintWithheld,
  logWithheldPromptSkipped,
  slashCommandPromptSlot,
} from '../../../redux/features/experiments/experiments.utils';
import { promptAsked } from '../../../redux/features/experiments/experiments.slice';
import { IPromptSlot } from '../../../redux/features/experiments/experiments.props';
import { selectBrandMenuConstraints } from '../../../selectors/menu';
import { IConstraint } from '../../../types/brandMenu';
import { ParsedModifierGroup } from '../../../types/menu';
import { upsell } from '../../../utils/upsell';
import { UpsellTriggers } from '../Omnibar.constants';
import { v4 as uuidv4 } from 'uuid';

const useAutocompleteMessage = () => {
  const dispatch = useAppDispatch();
  const [currentMessage, setCurrentMessage] = useState('');
  const { sendAgentInterception } = useAgentInterception();
  const isAgentActionBlocked = useAppSelector(selectIsAgentActionBlocked);
  const { total } = useSelector(selectOrderValues);
  const promptOverrides = useAppSelector(selectPromptOverrides);
  const promptAskCounts = useAppSelector(selectPromptAskCounts);
  const hasCheckTotal = useAppSelector(selectHasCheckTotal);
  const withheldConstraints = useAppSelector(selectWithheldConstraints);
  const brandMenuConstraints = useAppSelector(selectBrandMenuConstraints);
  const experimentAssignment = useAppSelector(selectExperimentAssignment);
  const updateCurrentMessage = (message: string) => {
    setCurrentMessage(message);
  };
  const sendAgentInterventionEvent = (
    type: AgentInterceptionType,
    description?: string
  ) => {
    sendAgentInterception({
      data: {
        type,
        description,
      },
    });
  };
  /**
   * HITL-1587: placeholder substitution, the no-total skip, and the
   * spoken-total bookkeeping all live in the shared sendSpokenPrompt thunk —
   * the same path the cart-add auto-prompt and the center-pane heading use.
   * Only the interception (whose side effects are surface-specific) is
   * stamped here, and only when something was actually spoken. Returns
   * whether the prompt went out.
   */
  const sendMessage = (
    message: string,
    metadata?: any,
    title?: string
  ): boolean => {
    // HITL-1606: outside Intercept the agent may not speak or send. Guarded
    // HERE, at the send, not only in useAgentInterception: these paths dispatch
    // the outbound frame FIRST and stamp the interception after, so refusing the
    // interception alone would still let the customer hear it. Reachable by
    // keyboard with no button to disable, so a refusal here is expected, not a
    // defect — debug, not warn.
    if (isAgentActionBlocked) {
      logger.debug({
        message: 'Omnibar send refused: the agent is not in Intercept mode',
      });
      return false;
    }
    const { spoken } = dispatch(sendSpokenPrompt({ message, metadata }));
    if (spoken) {
      sendAgentInterventionEvent(AgentInterceptionType.TTS_SENT);
    }
    return spoken;
  };

  /**
   * HITL-1570: send the donation upsell prompt, gated on the constraint's
   * `speak_total_in_upsell` condition — with the condition set and no check
   * total yet, the donation upsell is skipped entirely (nothing is sent, and
   * false is returned so the caller skips its side effects too). The `{total}`
   * substitution and the spoken-total bookkeeping live in the shared
   * sendSpokenPrompt thunk that sendMessage routes through (HITL-1587); its
   * own no-total gate also covers a `{total}`-templated prompt whose
   * constraint is missing the condition. Returns whether the prompt was sent.
   */
  const sendDonationPromptMessage = (
    command: string,
    speakTotalInUpsell: boolean | undefined,
    metadata?: any
  ): boolean => {
    if (shouldSkipDonationPrompt({ speakTotalInUpsell, total })) {
      return false;
    }
    return sendMessage(command, metadata);
  };

  /**
   * HITL-1570: should a direct selection of this item be skipped outright?
   * True only for a donation item whose constraint declares
   * speak_total_in_upsell while no check total exists — the same gate the
   * reminder queue applies. Callers must then neither speak nor add the item.
   */
  const isDonationPromptSkipped = (item: {
    name?: string;
    speakTotalInUpsell?: boolean;
  }): boolean => {
    if ((item.name ?? '').split(':')[0] !== SLASH_COMMAND_REMINDERS.DONATION) {
      return false;
    }
    return shouldSkipDonationPrompt({
      speakTotalInUpsell: item.speakTotalInUpsell,
      total,
    });
  };

  /**
   * Returns whether a prompt was actually sent — false when a gated donation
   * was skipped for lack of a check total or the prompt is withheld for this
   * car, so the caller must skip its own side effects (like adding the item to
   * the cart) too.
   */
  const sendSlashCommandMessage = (
    item: TopLevelMenuItem,
    isUnifiedMenuEnabled: boolean
  ): boolean => {
    const itemName = item.name;
    let intent = '';
    let sendVal = '';
    let slot: IPromptSlot | undefined;
    if (isUnifiedMenuEnabled) {
      if (
        isConstraintWithheld(withheldConstraints, item.activeConstraintName)
      ) {
        logWithheldPromptSkipped(item.activeConstraintName, 'slash command');
        return false;
      }
      intent = itemName.replace('/', '');
      if (item.slashCommands?.length) {
        slot = slashCommandPromptSlot(item);
        sendVal =
          getSlashCommandSpokenPrompt(
            promptOverrides,
            promptAskCounts,
            item,
            hasCheckTotal
          ) ?? '';
      }
    } else {
      [intent, sendVal] = itemName.replace('/', '').split(':');
    }
    // HITL-1733: a quick phrase whose constraint is an upsell prompt (CJ's
    // `/combo_large` → `combo_large_tts`) is an upsell offer, but went out with
    // only its intent — no attempt, so neither the offer nor the customer's
    // answer was ever recorded. The id goes on the spoken line so the TTS
    // acknowledgement emits the attempt METRIC, as on the auto-prompt path.
    const upsellConstraint: IConstraint | undefined = isUnifiedMenuEnabled
      ? brandMenuConstraints[item.activeConstraintName ?? '']
      : undefined;
    const upsellId = upsellConstraint?.is_upsell_prompt ? uuidv4() : undefined;
    const metadata = upsellId ? { intent, upsell_id: upsellId } : { intent };
    // HITL-1570: the donation prompt is gated on the check total when its
    // constraint declares speak_total_in_upsell, and tracks whether the
    // total was spoken.
    const spoken =
      itemName.split(':')[0] === SLASH_COMMAND_REMINDERS.DONATION
        ? sendDonationPromptMessage(sendVal, item.speakTotalInUpsell, metadata)
        : sendMessage(sendVal, metadata);
    // Opened only once the prompt is spoken: a refused or skipped prompt must
    // not leave an attempt for settle() to auto-decline. The acknowledgement
    // arrives over the socket, so the attempt is in place before it does.
    if (spoken && upsellConstraint && upsellId) {
      const { name, upsell_items = [], child_items = [] } = upsellConstraint;
      upsell.attempt(
        {
          ...upsellConstraint,
          upsell_items,
          // Same derivation as parseModifierGroup, so a tick is matched the
          // way it is for an auto-prompted upsell.
          upsell_decline_items: child_items.filter(
            (childItem) => !upsell_items.includes(childItem)
          ),
        } as unknown as ParsedModifierGroup,
        '',
        UpsellTriggers.hitlOmnibar,
        sendVal,
        name,
        upsellId,
        buildUpsellExperimentStamp(experimentAssignment, name)
      );
    }
    if (spoken && slot) dispatch(promptAsked(slot, item.slashCommands));
    return spoken;
  };

  return {
    currentMessage,
    updateCurrentMessage,
    sendMessage,
    sendDonationPromptMessage,
    isDonationPromptSkipped,
    sendAgentInterventionEvent,
    sendSlashCommandMessage,
  };
};

export default useAutocompleteMessage;
